import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, FormState, useForm } from 'react-hook-form';
import { z } from 'zod';
import { Box, Text, TextField } from '@streamelements/frontend-ui';
import { ResolutionComboBox } from '../-ResolutionComboBox';

const validationSchema = z.object({
  canvasName: z.string().min(1),
  videoFrame: z.object({
    width: z.number(),
    height: z.number(),
  }),
});

type FormValues = z.infer<typeof validationSchema>;

type Props = {
  onSubmit(values: FormValues): void;
  children: (props: { formState: FormState<FormValues> }) => React.ReactNode;
};

export function AddCanvasForm({ onSubmit, children }: Props) {
  const form = useForm<FormValues>({
    defaultValues: { canvasName: 'New canvas', videoFrame: { width: 1920, height: 1080 } },
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
  });

  return (
    <Box
      css={{ display: 'flex', flexDirection: 'column', gap: '$base', padding: '1.5rem' }}
      as="form"
      onSubmit={form.handleSubmit(onSubmit)}
    >
      <Box css={{ mb: '$16' }}>
        <Text.Heading weight="bold" level={5}>
          Add canvas
        </Text.Heading>
        <Text.Body>Add an canvas to multi-stream to sources</Text.Body>
      </Box>

      <Controller
        name="canvasName"
        control={form.control}
        render={({ field: { name, onChange, value }, fieldState: { invalid, error } }) => {
          return (
            <TextField.Single
              label="Name"
              value={value}
              onChange={onChange}
              name={name}
              css={{ mb: '1rem' }}
              error={invalid}
            >
              {Boolean(error?.message) && <TextField.HelperText>{error?.message}</TextField.HelperText>}
            </TextField.Single>
          );
        }}
      />

      <Controller
        control={form.control}
        name="videoFrame"
        render={({ field }) => {
          return (
            <Box css={{ mb: '1rem' }}>
              <ResolutionComboBox
                value={`${field.value.width}x${field.value.height}`}
                onValueChange={({ width, height }) => {
                  form.setValue('videoFrame', {
                    ...field.value,
                    width,
                    height,
                  });
                }}
              />
            </Box>
          );
        }}
      />

      <Box
        css={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
      >
        {children({
          formState: form.formState,
        })}
      </Box>
    </Box>
  );
}
